import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { shopApi } from '@wa/shared';
import type { ShopImportPreview } from '@wa/shared';
import { Card, Notice, errorText, primary, secondary } from './ui';

/**
 * Products and prices from a spreadsheet. Always a dry run first: the client sees
 * every row's fate before anything is written, and Apply does exactly that.
 */
export function ImportPanel() {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ShopImportPreview | null>(null);
  const [applied, setApplied] = useState<ShopImportPreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: (f: File) => shopApi.importPreview(f),
    onSuccess: (p) => { setPreview(p); setApplied(null); setError(null); },
    onError: (e) => setError(errorText(e, 'Could not read the file.')),
  });
  const apply = useMutation({
    mutationFn: (f: File) => shopApi.importApply(f),
    onSuccess: (p) => { setApplied(p); setPreview(null); setError(null); void queryClient.invalidateQueries({ queryKey: ['shop'] }); },
    onError: (e) => setError(errorText(e, 'Import failed; nothing was changed.')),
  });

  const sample = async () => {
    const { blob, fileName } = await shopApi.importSample();
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = fileName; a.click(); URL.revokeObjectURL(a.href);
  };

  return (
    <div className="flex flex-col gap-4">
      {error && <Notice kind="bad">{error}</Notice>}
      <Card title="Import products" blurb="CSV or Excel. Columns: product, variant, sku, category, description, unit, hsn, tax_rate, list_price, stock, and one price:<customer type> column per type. A product that already exists (same name) is updated; a new one needs list_price."
            actions={<button type="button" className={secondary} onClick={() => void sample()}>Download sample</button>}>
        <div className="flex flex-wrap items-center gap-2">
          <input type="file" accept=".csv,.xlsx,.xls,text/csv" className="text-small" onChange={(e) => { setFile(e.target.files?.[0] ?? null); setPreview(null); setApplied(null); }} />
          <button type="button" className={primary} disabled={!file || run.isPending} onClick={() => file && run.mutate(file)}>{run.isPending ? 'Checking…' : 'Check the file'}</button>
        </div>
      </Card>

      {preview && (
        <Card title={preview.fileError ? 'The file cannot be imported' : `Ready: ${preview.creates} new, ${preview.updates} updated, ${preview.errors} skipped`}
              blurb={preview.fileError ?? (preview.groups.length ? `Prices for: ${preview.groups.join(', ')}.` : 'No price columns — list prices only.')}
              actions={!preview.fileError && preview.creates + preview.updates > 0
                ? <button type="button" className={primary} disabled={apply.isPending} onClick={() => file && apply.mutate(file)}>{apply.isPending ? 'Importing…' : `Import ${preview.creates + preview.updates} rows`}</button>
                : undefined}>
          <Outcomes preview={preview} />
        </Card>
      )}

      {applied && (
        <Card title={`Imported: ${applied.creates} new, ${applied.updates} updated${applied.errors ? `, ${applied.errors} skipped` : ''}`}>
          {applied.errors > 0 && <Outcomes preview={applied} errorsOnly />}
          {applied.errors === 0 && <Notice kind="good">Every row went in. The page shows the new catalogue on its next load.</Notice>}
        </Card>
      )}
    </div>
  );
}

function Outcomes({ preview, errorsOnly }: { preview: ShopImportPreview; errorsOnly?: boolean }) {
  const rows = errorsOnly ? preview.outcomes.filter((o) => o.action === 'error') : preview.outcomes;
  if (rows.length === 0) return null;
  return (
    <div className="max-h-96 overflow-auto">
      <table className="w-full text-small">
        <thead><tr className="text-left text-micro uppercase tracking-wide text-(--color-faint)"><th className="py-1 pr-3">Row</th><th className="py-1 pr-3">Product</th><th className="py-1 pr-3">Variant</th><th className="py-1 pr-3">Outcome</th></tr></thead>
        <tbody>
          {rows.slice(0, 500).map((o) => (
            <tr key={o.row} className="border-t border-(--color-line-dim)">
              <td className="py-1 pr-3 tabular-nums text-(--color-muted)">{o.row}</td>
              <td className="py-1 pr-3">{o.product ?? '—'}</td>
              <td className="py-1 pr-3 text-(--color-muted)">{o.variant ?? ''}</td>
              <td className={`py-1 pr-3 ${o.action === 'error' ? 'text-(--color-bad)' : o.action === 'create' ? 'text-(--color-good)' : ''}`}>{o.error ?? o.action}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 500 && <p className="mt-1 text-micro text-(--color-muted)">Showing the first 500 of {rows.length}.</p>}
    </div>
  );
}
